import { useMemo } from 'react'

import { useParams } from 'react-router'

import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useCreators } from '@/modules/creators/hooks/useCreators.ts'
import { CreatorCombobox } from '@/modules/overview/ui/CreatorCombobox.tsx'
import { ChatterInspectCards } from '@/modules/tracking/chatter-tracking/components/ChatterInspectCards'
import { useChatterInspectData } from '@/modules/tracking/chatter-tracking/query/chatter-tracking-inspect-query.ts'
import {
  mapChatterKeystrokes,
  mapChatterOnlineTime,
  mapChatterPpvPurchase,
  mapChatterSentMessages,
  mapChatterTotalSales,
} from '@/modules/tracking/chatter-tracking/utils'
import { AppRoutes } from '@/utils/constants'
import { useCreatorUrl } from '@/utils/creators.ts'

export const ChatterInspectView = () => {
  const { date, setDate, isOneDay } = useDateRangeContext()
  const { userSettings } = useAccount()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const parameters = useParams()
  const {
    selection: {
      allSelect,
      allRemove,
      addSelect,
      removeSelect,
      selectableCreators,
      selectedCreators,
      setSelect,
    },
  } = useCreators()

  // Quick fix for edge case...
  const { data: response, isFetching } = useChatterInspectData(
    selectedCreators?.length > 0 ? selectedCreators[0] : undefined,
    date,
    parameters.userId ? parseInt(parameters.userId) : undefined,
    parameters.name && !parameters.userId
      ? decodeURI(parameters.name || '').replace('[slash]', '/')
      : undefined,
    isOneDay,
  )
  const { timezoneOffset, name, data } = response ?? {}

  const {
    totalSalesData,
    ppvPurchaseData,
    sentMessagesData,
    keystrokesData,
    onlineTimeData,
  } = useMemo(() => {
    return {
      totalSalesData: mapChatterTotalSales(data, isOneDay, shouldShowCents),
      ppvPurchaseData: mapChatterPpvPurchase(data, isOneDay),
      sentMessagesData: mapChatterSentMessages(data, isOneDay),
      keystrokesData: mapChatterKeystrokes(data, isOneDay),
      onlineTimeData: mapChatterOnlineTime(data),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isOneDay])

  const listUrl = useCreatorUrl(AppRoutes.Creator.ChatterTracking.Chatter.List)

  return (
    <Page
      backToPage={{
        name: 'Chatter Tracking',
        url: listUrl,
      }}
      title={`Chatter Tracking ${name ? `(${name})` : ''}`}
      headerOptions={
        <>
          <CalendarRangeDatePicker
            date={date}
            setDate={setDate}
            dateNameSuffix={timezoneOffset ? `(${timezoneOffset})` : undefined}
          />

          <CreatorCombobox
            selectable={selectableCreators}
            selected={selectedCreators}
            onAllSelect={allSelect}
            onAllRemove={allRemove}
            onAddSelect={addSelect}
            onRemoveSelect={removeSelect}
            setSelect={setSelect}
            selectMultiple={false}
            showAvatar={true}
          />
        </>
      }
    >
      <ChatterInspectCards
        totalSalesData={totalSalesData}
        ppvPurchaseData={ppvPurchaseData}
        sentMessagesData={sentMessagesData}
        keystrokesData={keystrokesData}
        onlineTimeData={onlineTimeData}
        isFetching={isFetching}
        showCents={shouldShowCents}
      />
    </Page>
  )
}
